import type { CatalogProduct, Cta } from '@/lib/api/catalog';
import type { CreateOrderResponse } from '@/lib/api/order';
import { groupByCategory } from '@/lib/catalog/present';
import { formatWon, surchargeBillingNote, surchargeNote } from '@/lib/catalog/format';
import { formatKrw, leadTimeTextEn, surchargeBillingNoteEn, surchargeNoteEn } from '@/lib/catalog/format.en';
import type { SiteLocale } from '@/lib/constants/locale';

/**
 * 상담 신청 「바로 결제」(계획서 §3.9 N2·N3)가 **서버 카탈로그**(`catalogApi.getProducts`, 53종)를 다루는 계산 —
 * 분류 목록·검색·언어별 문구·접수 본문·주문 응답 검증. 컴포넌트 밖에 두는 이유는 이 레포의 테스트가 node 환경에서
 * `.ts` 만 돌리기 때문이다.
 *
 * 전에는 기획 `script.js` 의 목업 20종(`service-catalog.ts`)이 여기 있었고 옵션 합산(`quoteOf`)도 있었다.
 * 서버 상품에는 옵션 그룹이 없다 — 금액은 서버가 확정해 내려주고(`supplyAmount`·`vatAmount`·`totalAmount`),
 * 화면은 표기만 한다(`lib/catalog/format`·`format.en`). **여기서 금액을 계산하지도, 금액 문자열을 직접 조립하지도 않는다.**
 */

export type CategoryFilter = string | 'ALL';

export interface CatalogCategory {
  code: string;
  /** 서버가 요청 언어로 준 `categoryLabel`. 메시지 카탈로그에 분류명을 두지 않는다 — 분류는 운영자가 배포 없이 늘린다. */
  label: string;
}

/** 분류 목록. **서버가 준 순서 그대로**(`sortOrder` 에 운영자의 의도가 있다). */
export const categoriesOf = (products: readonly CatalogProduct[]): CatalogCategory[] => groupByCategory([...products]).map(group => ({ code: group.categoryCode, label: group.heading }));

/** 공백을 지우고 소문자로 — 사람은 「계약서검토」라고 붙여 쓰고 카탈로그에는 「계약서 검토 · 국문 표준」으로 들어 있다. */
const normalize = (text: string): string => text.replace(/\s+/g, '').toLowerCase();

/**
 * 검색어·분류로 거른다. 검색은 상품명·한 줄 설명·분류명·코드·받으실 것·납기를 훑는다(`present.ts` 의 `searchProducts` 는
 * 이름·코드만 보고 빈 검색어에 빈 배열을 주어 목록 화면에는 못 쓴다). 순서는 서버 순서 그대로다.
 *
 * 서비스 선택과 요금 안내 단건 카드(`marketing/pricing/PriceGuide`)가 **같이 쓴다.** 요금 안내는 예전에 표의 「기준」 문구까지
 * 찾았다 — 사람은 「검토의견서」처럼 받을 문서 이름으로도 찾는다. 그래서 받으실 것·납기를 넣었다. **할증 문구는 넣지 않는다** —
 * 「30장 이상은 상담·견적」이 들어 있어 「상담」을 치면 할증 상품이 전부 걸린다.
 *
 * **납기는 화면에 보이는 문구로 찾는다(`leadTimeTextOf`).** 서버의 `leadTimeLabel` 은 언어와 상관없이 국문(「2~3영업일」)이라
 * 그대로 넣으면 영문 서비스 선택에서 카드에 찍힌 「2–3 business days」로는 안 걸린다. 그래서 `locale` 을 필수로 받는다 —
 * 선택으로 두면 호출부가 빠뜨려도 조용히 국문으로 돈다. 받으실 것·분류명·설명은 서버가 요청 언어로 준다.
 */
export const filterProducts = (products: readonly CatalogProduct[], query: string, category: CategoryFilter, locale: SiteLocale): CatalogProduct[] => {
  const needle = normalize(query);
  return products.filter(product => {
    if (category !== 'ALL' && product.categoryCode !== category) return false;
    if (!needle) return true;
    const haystack = [product.code, product.nameKo, product.nameEn ?? '', product.categoryLabel, product.description ?? '', ...product.deliverables, leadTimeTextOf(product, locale)];
    return normalize(haystack.join(' ')).includes(needle);
  });
};

/**
 * 화면에 그릴 이름. 국문 화면은 `nameKo` 다 — 서버가 국문 응답에 `nameEn` 을 싣기 시작해도 국문 목록에 영문명이 섞이지 않게.
 * 영문 화면은 `nameEn`, 비어 있으면 국문으로 떨어뜨린다(`format.en.ts` 의 `productNameEn` 과 같은 규칙 — 행이 사라지는 것보다 낫다).
 */
export const displayNameOf = (product: CatalogProduct, locale: SiteLocale): string => (locale === 'en' ? product.nameEn?.trim() || product.nameKo : product.nameKo);

/**
 * 금액 문자열. **화면이 직접 `toLocaleString()` 을 부르지 않는다** — 요금표·챗봇과 같은 함수(`formatWon`·`formatKrw`)를 거쳐야
 * 세 화면의 표기가 갈리지 않고, 로케일을 명시하지 않은 `toLocaleString()` 은 서버(Node ICU)와 브라우저가 달라 하이드레이션이 어긋난다.
 */
export const moneyTextOf = (amount: number, locale: SiteLocale): string => (locale === 'en' ? formatKrw(amount) : formatWon(amount));

/**
 * 납기 문구. `leadTimeLabel` 은 국문 자유문(「2~3영업일」·「일정 협의」)이라 영문 화면에서는 `format.en` 이 숫자로 다시 쓴다 —
 * 국문 그대로 내보내면 영문 카드에 「2~3영업일」이 섞인다.
 */
export const leadTimeTextOf = (product: CatalogProduct, locale: SiteLocale): string => (locale === 'en' ? leadTimeTextEn(product) : product.leadTimeLabel);

/**
 * 분량 기준 한 줄. 할증이 없는 상품은 `null`.
 *
 * 결제 화면(`isOrder`)은 「지금 결제하는 금액이 어디까지인가」(초과분은 검토 후 청구)를, 접수는 「얼마인가」를 말한다 —
 * `lib/catalog/format` 의 두 문장을 언어에 맞춰 고른다. 영문 단위는 서버가 단수형(`page`)으로 주고 복수는 `format.en` 이 만든다.
 */
export const volumeNoteOf = (product: CatalogProduct, locale: SiteLocale, isOrder: boolean): string | null => {
  if (locale === 'en') return isOrder ? surchargeBillingNoteEn(product) : surchargeNoteEn(product);
  return isOrder ? surchargeBillingNote(product) : surchargeNote(product);
};

/**
 * 이 화면이 실제로 따를 `cta`. **영문 화면에서는 `ORDER` 를 `INTAKE` 로 접는다.**
 *
 * 주문 생성은 서버가 `LK_LOCALE` **쿠키**로 로케일을 판정하는데, 그 쿠키는 이 사이트 호스트에만 붙어 있고 주문 요청은
 * 다른 오리진(게이트웨이)으로 나가 서버에 닿지 않는다(`axios.ts` 에 `withCredentials` 없음). 그래서 영문 화면의 주문은
 * **예외 없이** 국문 주문으로 돌아오고, 그대로 열면 화면과 다른 금액을 청구하고 막으면 누를 때마다 미결제 주문만 쌓인다.
 * 어느 쪽도 안 되니 영문에서는 결제 버튼을 아예 그리지 않고 빠른 접수로 보낸다 — 영문 요금표(`format.en.ts`)도 같은 전제
 * (영문 페이지에는 주문 깔때기가 없다)로 쓰여 있다. 서버가 인증 주체의 언어 설정을 보게 되면 이 접기를 푼다.
 */
export const effectiveCtaOf = (product: Pick<CatalogProduct, 'cta'>, locale: SiteLocale): Cta => (locale === 'en' && product.cta === 'ORDER' ? 'INTAKE' : product.cta);

/**
 * 결제 대상이 아닌 상품(실효 `cta` 가 `INTAKE`·`INQUIRY`)을 빠른 접수로 보낼 때 요청사항 뒤에 붙이는 선택 내용.
 * 변호사가 읽는 본문이라 한국어 고정(서버 데이터). 금액은 서버 게시가 그대로다 — 견적의 출발점이지 확정이 아니다.
 * 영문 화면에서 들어온 접수는 게시가가 **영문가(배율 반영)** 라 그 사실을 적는다 — 접수 레코드에 로케일이 없어 나중에 구분할 수 없다.
 *
 *   [서비스 선택] 계약서 검토 · 국문 표준 (10p 이하) (CONT-01)
 *   분류: 계약
 *   게시가: 400,000원 (VAT 포함 440,000원)
 *   완료 예정: 2~3영업일
 */
export const intakeSummaryOf = (product: CatalogProduct, locale: SiteLocale = 'ko'): string =>
  [
    `[서비스 선택] ${product.nameKo} (${product.code})`,
    `분류: ${product.categoryLabel}`,
    `${locale === 'en' ? '게시가(영문 화면 · 영문가 기준)' : '게시가'}: ${formatWon(product.supplyAmount)} (VAT 포함 ${formatWon(product.totalAmount)})`,
    `완료 예정: ${product.leadTimeLabel}`,
  ].join('\n');

export type OrderMismatch = 'locale' | 'amount';

/**
 * 주문 응답이 화면에 보인 상품과 같은 것인가.
 *
 * 로케일 불일치는 정상 경로에서는 나오지 않는다(`effectiveCtaOf` 가 영문 결제를 접는다) — 남겨 두는 것은 마지막 방어선이다.
 * 로케일이 같아도 금액이 다르면(그 사이 가격이 바뀜) 화면과 다른 것이다.
 */
export const orderMismatchOf = (order: Pick<CreateOrderResponse, 'locale' | 'totalAmount'>, product: Pick<CatalogProduct, 'totalAmount'>, locale: SiteLocale): OrderMismatch | null => {
  if (order.locale !== locale) return 'locale';
  if (order.totalAmount !== product.totalAmount) return 'amount';
  return null;
};

/**
 * 주문 응답을 받고 화면이 할 일.
 *
 *   - `pay`            화면과 같다 → 결제창을 연다.
 *   - `confirmAmount`  로케일은 같은데 금액만 다르다 → 결제창을 바로 열지 않고 **실제 주문 금액**을 보여 준 뒤 그 금액으로
 *                      이어갈지 묻는다. 결제창을 닫아 둔 사이 가격이 바뀐 기존 미결제 주문(`reused`)이 대표적이고, 화면이
 *                      낡은 채 새 주문이 만들어진 경우도 같다 — 어느 쪽이든 서버 금액이 진짜고 고객이 그 숫자를 보고 고른다.
 *                      막기만 하면 기존 주문은 영영 못 낸다(취소 API 가 없다).
 *   - `blockLocale`    다른 언어 가격 정책의 주문이다 → 열지 않는다. 금액을 보여 주고 동의를 받는 것으로는 부족하다 —
 *                      상품명·가격표 자체가 다른 판매 구성이고, 금액이 우연히 같으면 안내마저 거짓이 된다.
 */
export type OrderResolution = 'pay' | 'confirmAmount' | 'blockLocale';

export const orderResolutionOf = (order: Pick<CreateOrderResponse, 'locale' | 'totalAmount'>, product: Pick<CatalogProduct, 'totalAmount'>, locale: SiteLocale): OrderResolution => {
  const mismatch = orderMismatchOf(order, product, locale);
  if (mismatch === 'locale') return 'blockLocale';
  if (mismatch === 'amount') return 'confirmAmount';
  return 'pay';
};
